import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { format, parseISO, startOfMonth, endOfMonth, differenceInMinutes } from 'date-fns';
import { DollarSign, Calendar } from 'lucide-react';

interface TimeLog {
  id: string;
  event_type: 'arrival' | 'departure';
  scanned_at: string;
}

interface EmployeeData {
  id: string;
  name: string;
  salary_adjustment: number;
  employment_types?: {
    default_pay_rate: number;
    pay_type: string;
  };
  roles?: {
    default_pay_rate: number;
    pay_type: string;
  };
}

interface PayrollData {
  totalHours: number;
  totalEarnings: number;
  workingSessions: WorkingSession[];
}

interface WorkingSession {
  date: string;
  arrivalTime?: string;
  departureTime?: string;
  hoursWorked: number;
  earnings: number;
  status: 'complete' | 'incomplete';
}

interface EmployeePayrollTabProps {
  employeeId: string;
  businessId: string;
}

const EmployeePayrollTab: React.FC<EmployeePayrollTabProps> = ({ employeeId, businessId }) => {
  const [employee, setEmployee] = useState<EmployeeData | null>(null);
  const [payrollData, setPayrollData] = useState<PayrollData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [paymentsEnabled, setPaymentsEnabled] = useState<boolean | null>(null);

  // Fetch payment processing status for the business
  useEffect(() => {
    const fetchPaymentStatus = async () => {
      if (!businessId) return;

      try {
        const { data, error } = await supabase
          .from('businesses')
          .select('payment_processing_enabled')
          .eq('id', businessId)
          .single();

        if (error) throw error;
        setPaymentsEnabled(data?.payment_processing_enabled || false);
      } catch (error) {
        console.error('Error fetching payment status:', error);
        setPaymentsEnabled(false);
      }
    };

    fetchPaymentStatus();
  }, [businessId]);

  useEffect(() => {
    const fetchEmployeeAndPayrollData = async () => {
      if (!employeeId) {
        setError('Employee ID not provided.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Fetch employee data with pay rate from employment_types and roles
        const { data: employeeData, error: employeeError } = await supabase
          .from('employees')
          .select(`
            id,
            name,
            salary_adjustment,
            employment_types (
              default_pay_rate,
              pay_type
            ),
            roles (
              default_pay_rate,
              pay_type
            )
          `)
          .eq('id', employeeId)
          .single();

        if (employeeError) {
          throw new Error(`Error fetching employee data: ${employeeError.message}`);
        }

        if (!employeeData) {
          throw new Error('Employee not found.');
        }

        setEmployee(employeeData);

        // Calculate effective hourly rate
        const effectiveHourlyRate = getEffectiveHourlyRate(employeeData);

        // Calculate month range
        const monthStart = startOfMonth(selectedMonth);
        const monthEnd = endOfMonth(selectedMonth);

        // Fetch time logs for the selected month
        const { data: timeLogsData, error: timeLogsError } = await supabase
          .from('time_logs')
          .select('id, event_type, scanned_at')
          .eq('employee_id', employeeId)
          .gte('scanned_at', monthStart.toISOString())
          .lte('scanned_at', monthEnd.toISOString())
          .order('scanned_at', { ascending: true });

        if (timeLogsError) {
          throw new Error(`Error fetching time logs: ${timeLogsError.message}`);
        }

        const timeLogs = timeLogsData || [];
        const payrollData = calculatePayrollData(timeLogs, effectiveHourlyRate);
        setPayrollData(payrollData);

      } catch (err) {
        console.error('Failed to fetch payroll data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load payroll data.');
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeAndPayrollData();
  }, [employeeId, selectedMonth]);

  const getEffectiveHourlyRate = (employee: EmployeeData): number => {
    // Priority: Role pay rate > Employment type pay rate > 0
    let baseRate = 0;

    if (employee.roles?.default_pay_rate) {
      baseRate = employee.roles.default_pay_rate;
    } else if (employee.employment_types?.default_pay_rate) {
      baseRate = employee.employment_types.default_pay_rate;
    }

    // Apply salary adjustment (could be positive or negative)
    const adjustment = employee.salary_adjustment || 0;
    return Math.max(0, baseRate + adjustment); // Ensure non-negative rate
  };

  const calculatePayrollData = (timeLogs: TimeLog[], hourlyRate: number): PayrollData => {
    // Group time logs by date
    const logsByDate: { [date: string]: TimeLog[] } = {};

    timeLogs.forEach(log => {
      const date = format(parseISO(log.scanned_at), 'yyyy-MM-dd');
      if (!logsByDate[date]) {
        logsByDate[date] = [];
      }
      logsByDate[date].push(log);
    });

    const workingSessions: WorkingSession[] = [];
    let totalHours = 0;

    Object.entries(logsByDate).forEach(([date, logs]) => {
      // Sort logs by time
      const sortedLogs = logs.sort((a, b) =>
        parseISO(a.scanned_at).getTime() - parseISO(b.scanned_at).getTime()
      );

      const arrivals = sortedLogs.filter(log => log.event_type === 'arrival');
      const departures = sortedLogs.filter(log => log.event_type === 'departure');

      let sessionHours = 0;
      let arrivalTime: string | undefined;
      let departureTime: string | undefined;
      let status: 'complete' | 'incomplete' = 'incomplete';

      if (arrivals.length > 0 && departures.length > 0) {
        // Take first arrival and last departure of the day
        const firstArrival = arrivals[0];
        const lastDeparture = departures[departures.length - 1];

        arrivalTime = firstArrival.scanned_at;
        departureTime = lastDeparture.scanned_at;

        const minutesWorked = differenceInMinutes(
          parseISO(lastDeparture.scanned_at),
          parseISO(firstArrival.scanned_at)
        );

        sessionHours = minutesWorked / 60;
        status = 'complete';
      } else if (arrivals.length > 0) {
        arrivalTime = arrivals[0].scanned_at;
        status = 'incomplete';
      }

      if (arrivalTime) {
        totalHours += sessionHours;
        workingSessions.push({
          date,
          arrivalTime,
          departureTime,
          hoursWorked: sessionHours,
          earnings: sessionHours * hourlyRate,
          status
        });
      }
    });

    // Sort sessions by date (newest first)
    workingSessions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    return {
      totalHours,
      totalEarnings: totalHours * hourlyRate,
      workingSessions
    };
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatHours = (hours: number) => {
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);

    if (minutes === 0) {
      return `${wholeHours}h`;
    }
    return `${wholeHours}h ${minutes}m`;
  };

  // Show loading while checking payment status
  if (paymentsEnabled === null) {
    return (
      <Card className="w-full max-w-4xl mx-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Loading...
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p>Checking payroll settings...</p>
        </CardContent>
      </Card>
    );
  }

  if (!paymentsEnabled) {
    return (
      <Card className="w-full max-w-4xl mx-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-muted-foreground" />
            My Payroll
          </CardTitle>
          <CardDescription>View your earnings and payroll information</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 space-y-4">
            <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center">
              <DollarSign className="h-8 w-8 text-muted-foreground" />
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-medium text-foreground">Payroll Not Available</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                The payroll system is not enabled for this business. Please contact your administrator for more information.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="w-full max-w-4xl mx-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Loading Payroll...
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p>Please wait while your payroll information is being loaded.</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-500 w-full max-w-4xl mx-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
          <CardTitle className="text-sm font-medium text-red-400 flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Error Loading Payroll
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-red-300">{error}</p>
          <p className="text-red-300 mt-2">Please try again later.</p>
        </CardContent>
      </Card>
    );
  }

  if (!employee || !payrollData) {
    return (
      <Card className="w-full max-w-4xl mx-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            My Payroll
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p>No payroll data available.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6 w-full max-w-md mx-auto">
      {/* Summary Card */}
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            My Payroll - {format(selectedMonth, 'MMMM yyyy')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Monthly Earnings</p>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(payrollData.totalEarnings)}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Hours</p>
              <p className="text-2xl font-bold">
                {formatHours(payrollData.totalHours)}
              </p>
            </div>
          </div>
          <div className="pt-2 border-t">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Hourly Rate:</span>
              <span className="font-semibold">{formatCurrency(getEffectiveHourlyRate(employee))}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Working Days:</span>
              <span className="font-semibold">{payrollData.workingSessions.length}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Working Sessions */}
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Working Sessions
          </CardTitle>
          <CardDescription>
            Your daily time tracking and earnings breakdown.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {payrollData.workingSessions.length === 0 ? (
            <p className="text-muted-foreground">No working sessions found for this month.</p>
          ) : (
            <div className="space-y-3">
              {payrollData.workingSessions.map((session, index) => (
                <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="font-medium">
                          {format(parseISO(session.date), 'EEEE, MMMM d, yyyy')}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                          {session.arrivalTime && (
                            <span>Arrived: {format(parseISO(session.arrivalTime), 'h:mm a')}</span>
                          )}
                          {session.departureTime && (
                            <span>Left: {format(parseISO(session.departureTime), 'h:mm a')}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="font-medium">{formatHours(session.hoursWorked)}</p>
                        <p className="text-lg font-bold text-green-600">
                          {formatCurrency(session.earnings)}
                        </p>
                      </div>
                      <Badge variant={session.status === 'complete' ? 'default' : 'secondary'}>
                        {session.status === 'complete' ? 'Complete' : 'Incomplete'}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmployeePayrollTab;
